import { useState, useRef, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { Mic, MicOff, Loader2, PhoneOff, Globe } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { LanguageSwitcher } from "./LanguageSwitcher";
import ClusterOpportunityBanner from "./ClusterOpportunityBanner";

import { BACKEND_URL, API, WS_URL } from '../utils/apiConfig';

const LANGUAGE_FLAGS = {
  de: "🇩🇪", tr: "🇹🇷", ar: "🇸🇦", en: "🇬🇧", pl: "🇵🇱",
  pt: "🇧🇷", fr: "🇫🇷", it: "🇮🇹", uk: "🇺🇦", es: "🇪🇸"
};

export const VoiceMatch = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [aiResponse, setAiResponse] = useState("");
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [extractedInfo, setExtractedInfo] = useState(null);

  // Voice session for multi-turn AI conversation
  const [voiceSessionId, setVoiceSessionId] = useState("");

  const [voiceChatActive, setVoiceChatActive] = useState(false);
  const [voiceChatStatus, setVoiceChatStatus] = useState("");
  const [aiSpeaking, setAiSpeaking] = useState(false);
  const [showLanguageSelector, setShowLanguageSelector] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState("de");
  const [availableLanguages, setAvailableLanguages] = useState({});
  const [roomId, setRoomId] = useState("");
  const [usersInRoom, setUsersInRoom] = useState(0);
  const [translationInfo, setTranslationInfo] = useState(null);
  
  const wsRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);

  const [audioLevel, setAudioLevel] = useState(0);
  useEffect(() => {
    if (!isListening) {
      setAudioLevel(0);
      return;
    }
    const interval = setInterval(() => {
      setAudioLevel(Math.random() * 100);
    }, 100);
    return () => clearInterval(interval);
  }, [isListening]);

  useEffect(() => {
    axios
      .get(`${API}/languages`)
      .then((res) => setAvailableLanguages(res.data.languages || {}))
      .catch((err) => console.error("Failed to load languages:", err));
  }, []);

  // Cleanup mic stream on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const sendAudioToWhisper = useCallback(async (audioBlob) => {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem('token');
      const lang = i18n.language?.split('-')[0] || 'de';
      const formData = new FormData();
      formData.append('file', audioBlob, 'recording.webm');
      formData.append('language', lang);
      if (voiceSessionId) formData.append('session_id', voiceSessionId);

      const res = await axios.post(`${API}/voice/chat`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      const { transcript: text, ai_response, session_id } = res.data;
      if (text) setTranscript(text);
      if (ai_response) setAiResponse(ai_response);
      if (session_id) setVoiceSessionId(session_id);
    } catch (err) {
      console.error("Whisper STT error:", err);
      setError(t('home.matchError'));
    } finally {
      setLoading(false);
    }
  }, [i18n.language, voiceSessionId, t]);

  const toggleListening = async () => {
    if (isListening) {
      // Stop recording — MediaRecorder.onstop will fire and send audio
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
      setIsListening(false);
    } else {
      setError("");
      setTranscript("");
      setAiResponse("");
      audioChunksRef.current = [];

      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        streamRef.current = stream;

        const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
          ? 'audio/webm;codecs=opus'
          : MediaRecorder.isTypeSupported('audio/webm')
            ? 'audio/webm'
            : '';

        const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
        mediaRecorderRef.current = recorder;

        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) audioChunksRef.current.push(e.data);
        };

        recorder.onstop = () => {
          // Release mic immediately
          stream.getTracks().forEach(track => track.stop());
          streamRef.current = null;

          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
          audioChunksRef.current = [];

          if (audioBlob.size > 0) {
            sendAudioToWhisper(audioBlob);
          }
        };

        recorder.start();
        setIsListening(true);
      } catch (err) {
        console.error("Mic access error:", err);
        setError(t('home.micError'));
      }
    }
  };
  
  const startVoiceChat = async (language) => {
    try {
      setVoiceChatStatus(t('home.connecting'));
      setShowLanguageSelector(false);
      setSelectedLanguage(language);
      
      const currentRoomId = roomId || `room-${Date.now()}`;
      setRoomId(currentRoomId);
      
      const ws = new WebSocket(`${WS_URL}/ws/voice-chat/${currentRoomId}/${language}`);
      wsRef.current = ws;

      ws.onopen = async () => {
        setVoiceChatActive(true);
        setVoiceChatStatus(t('home.connected'));
        
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
        mediaRecorderRef.current = mediaRecorder;
        
        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) audioChunksRef.current.push(event.data);
        };
        
        mediaRecorder.onstop = async () => {
          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
          audioChunksRef.current = [];
          const arrayBuffer = await audioBlob.arrayBuffer();
          if (ws.readyState === WebSocket.OPEN) ws.send(arrayBuffer);
        };
        
        const recordChunk = () => {
          if (mediaRecorder.state === "inactive" && voiceChatActive) {
            mediaRecorder.start();
            setTimeout(() => {
              if (mediaRecorder.state === "recording") {
                mediaRecorder.stop();
                setTimeout(recordChunk, 100);
              }
            }, 3000);
          }
        };
        recordChunk();
      };

      ws.onmessage = async (event) => {
        const data = JSON.parse(event.data);
        if (data.type === "connected") {
          setUsersInRoom(data.user_count);
          setVoiceChatStatus(`Verbunden mit Raum ${data.room_id}`);
        } else if (data.type === "translated_message") {
          setTranslationInfo({ from: data.from_language, original: data.original_text, translated: data.translated_text });
          setVoiceChatStatus(data.translated_text);
          if (data.audio) { setAiSpeaking(true); await playAudio(data.audio); setAiSpeaking(false); }
        } else if (data.type === "message_sent") {
          setVoiceChatStatus("\u2713 Nachricht gesendet");
        } else if (data.type === "error") {
          setVoiceChatStatus("Fehler: " + data.message);
        }
      };

      ws.onerror = () => setVoiceChatStatus("Verbindungsfehler");
      ws.onclose = () => stopVoiceChat();
    } catch (err) {
      console.error("Voice chat error:", err);
      setError("Voice Chat konnte nicht gestartet werden: " + err.message);
    }
  };

  const stopVoiceChat = () => {
    if (wsRef.current) { wsRef.current.close(); wsRef.current = null; }
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      mediaRecorderRef.current = null;
    }
    setVoiceChatActive(false);
    setVoiceChatStatus("");
    setAiSpeaking(false);
    setTranslationInfo(null);
  };

  const playAudio = async (base64Audio) => {
    return new Promise((resolve) => {
      const audioData = atob(base64Audio);
      const arrayBuffer = new ArrayBuffer(audioData.length);
      const uint8Array = new Uint8Array(arrayBuffer);
      for (let i = 0; i < audioData.length; i++) uint8Array[i] = audioData.charCodeAt(i);
      const blob = new Blob([arrayBuffer], { type: 'audio/mp3' });
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audio.onended = () => { URL.revokeObjectURL(url); resolve(); };
      audio.play();
    });
  };

  return (
    <div className="voice-match-page">
      <div className="container">
        {user?.firstName && (
          <div className="welcome-greeting" data-testid="welcome-greeting">
            <span>{t('common.welcomeBack', { name: user.firstName })}</span>
          </div>
        )}

        {/* Driver Activation: open cluster opportunities (Iter 97) */}
        <ClusterOpportunityBanner />

        <header className="header">
          <p className="subtitle">{t('home.subtitle')}</p>
        </header>

        <div className="voice-section">
          <div className={`voice-button-wrapper ${isListening ? 'active' : ''}`}>
            <div className={`audio-blob ${isListening ? 'active recording' : ''}`}></div>
            <div className={`sound-waves ${isListening ? 'active' : ''}`}>
              <div className="sound-wave"></div>
              <div className="sound-wave"></div>
              <div className="sound-wave"></div>
            </div>
            <button
              onClick={toggleListening}
              className={`voice-button ${isListening ? 'recording listening' : ''}`}
              disabled={loading}
              data-testid="voice-button"
            >
              {isListening ? (
                <><MicOff size={32} /><span>{t('home.voiceButton.stop')}</span></>
              ) : (
                <><Mic size={32} /><span>{t('home.voiceButton.speak')}</span></>
              )}
            </button>
          </div>

          {isListening && (
            <p className="listening-indicator" data-testid="listening-indicator">
              {t('home.listening')}
            </p>
          )}
        </div>
        
        <LanguageSwitcher variant="flags" />

        {transcript && (
          <div className="transcript-box" data-testid="transcript">
            <strong>{t('home.youSaid')}</strong>
            <p>"{transcript}"</p>
          </div>
        )}

        {aiResponse && (
          <div className="ai-response-box" data-testid="ai-response">
            <strong>VoiceRide AI</strong>
            <p>{aiResponse}</p>
          </div>
        )}

        {extractedInfo && (
          <div className="extracted-info" data-testid="extracted-info">
            <strong>{t('home.understood')}</strong>
            <div className="info-grid">
              {extractedInfo.start && <div><span>{t('common.from')}:</span> {extractedInfo.start}</div>}
              {extractedInfo.destination && <div><span>{t('common.to')}:</span> {extractedInfo.destination}</div>}
              {extractedInfo.time && <div><span>{t('common.time')}:</span> {extractedInfo.time}</div>}
            </div>
          </div>
        )}

        {loading && (
          <div className="loading" data-testid="loading">
            <Loader2 className="spinner" size={24} />
            <span>{t('home.searchingMatches')}</span>
          </div>
        )}

        {error && <div className="error-box" data-testid="error">{error}</div>}

        {matches.length > 0 && (
          <div className="matches-section" data-testid="matches">
            <h2>Gefundene Fahrten ({matches.length})</h2>
            
            <div className="voice-chat-section">
              {!voiceChatActive ? (
                <>
                  {!showLanguageSelector ? (
                    <button data-testid="start-voice-chat" onClick={() => setShowLanguageSelector(true)} className="voice-chat-button">
                      <span className="lang-flag">{LANGUAGE_FLAGS[selectedLanguage] || "\uD83C\uDF10"}</span>
                    </button>
                  ) : (
                    <div className="language-selector">
                      <h3>{t('home.selectLanguage')}</h3>
                      <div className="language-grid">
                        {Object.entries(availableLanguages).map(([code, lang]) => (
                          <button key={code} data-testid={`lang-${code}`} onClick={() => startVoiceChat(code)} className="language-option">
                            <Globe size={18} /><span>{lang.name}</span>
                          </button>
                        ))}
                      </div>
                      <button onClick={() => setShowLanguageSelector(false)} className="cancel-button">{t('common.cancel')}</button>
                    </div>
                  )}
                </>
              ) : (
                <div className="voice-chat-active">
                  <button data-testid="stop-voice-chat" onClick={stopVoiceChat} className="voice-chat-button stop">
                    <PhoneOff size={20} /><span>{t('home.endChat')}</span>
                  </button>
                  <div className="chat-info">
                    <div className="info-row"><span className="label">{t('home.yourLanguage')}</span><span className="value">{availableLanguages[selectedLanguage]?.name}</span></div>
                    <div className="info-row"><span className="label">{t('home.usersInRoom')}</span><span className="value">{usersInRoom}</span></div>
                    {roomId && <div className="info-row"><span className="label">{t('home.roomId')}</span><span className="value room-id">{roomId}</span></div>}
                  </div>
                  <div className="voice-chat-status">
                    <div className={`status-indicator ${aiSpeaking ? 'speaking' : 'listening'}`}></div>
                    <span>{aiSpeaking ? t('home.receiving') : t('home.listening')}</span>
                  </div>
                  {translationInfo && (
                    <div className="translation-info">
                      <div className="translation-header"><Globe size={16} /><span>{t('home.translationFrom')} {translationInfo.from}</span></div>
                      <p className="original-text">"{translationInfo.original}"</p>
                      <p className="translated-text">{"\u2192"} "{translationInfo.translated}"</p>
                    </div>
                  )}
                  {voiceChatStatus && !translationInfo && <p className="ai-message">{voiceChatStatus}</p>}
                </div>
              )}
            </div>

            <div className="matches-grid">
              {matches.map((match) => (
                <div key={match.user.id} className="match-card" data-testid={`match-${match.user.id}`}>
                  <div className="match-header">
                    <h3>{match.user.name}</h3>
                    <span className="score">{match.match_score}%</span>
                  </div>
                  <div className="match-details">
                    <div className="route">
                      <span className="from">{match.user.start_city}</span>
                      <span className="arrow">{"\u2192"}</span>
                      <span className="to">{match.user.destination}</span>
                    </div>
                    <div className="time">{match.user.departure_time}</div>
                    <div className="seats">{match.user.seats_available} {t('home.seatsAvailable')}</div>
                  </div>
                  <div className="match-reason">{match.reason}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {transcript && matches.length === 0 && !loading && (
          <div className="no-matches" data-testid="no-matches">{t('home.noMatches')}</div>
        )}
      </div>
    </div>
  );
};
